<template>
  <div>
    <modal-box
      :is-active="isModalActive"
      :trash-object-name="trashObjectName"
      @confirm="trashConfirm"
      @cancel="trashCancel"
    />

    <b-table
      v-bind="$attrs"
      scrollable
      pagination-size="is-small"
      ref="table"
      :detailed="hasDetail"
      :show-detail-icon="false"
      :default-sort="columns[0].key"
      :checked-rows.sync="checkedRows"
      :checkable="checkable"
      :loading="isLoading"
      :paginated="isPaginated"
      :backend-pagination="isPaginated"
      :total="total"
      :per-page="perPage"
      :striped="true"
      :hoverable="true"
      :data="data"
      @click="onRowClick"
      @page-change="onPageChange"
    >
      <b-table-column v-slot="props" centered sticky label="#">
        {{ props.index + 1 }}
      </b-table-column>
      <template v-for="column in columns">
        <b-table-column
          v-if="column.isSlot"
          v-slot="props"
          :key="column.key"
          :label="column.label"
          :width="column.width"
        >
          <slot :name="column.key" :row="props.row" />
        </b-table-column>
        <b-table-column
          v-else-if="column.content"
          v-slot="props"
          :key="column.key"
          :label="column.label"
          :visible="column.visible"
          :width="column.width"
        >
          <span v-html="column.content(props.row)"></span>
        </b-table-column>
        <b-table-column
          v-else
          v-slot="props"
          :key="column.key"
          :label="column.label"
          :visible="column.visible"
          :width="column.width"
        >
          {{ props.row[column.key] }}
        </b-table-column>
      </template>

      <b-table-column
        custom-key="actions"
        class="is-actions-cell"
        v-slot="props"
      >
        <div class="buttons is-right">
          <slot name="actions" :row="props.row" />
          <router-link
            v-if="hasUpdate"
            :to="updateTo(props.row)"
            class="button is-small is-primary"
          >
            <b-icon icon="pencil" size="is-small" />
          </router-link>
          <button
            v-if="hasDelete"
            class="button is-small is-danger"
            type="button"
            @click.prevent="trashModal(props.row)"
          >
            <b-icon icon="trash-can" size="is-small" />
          </button>
        </div>
      </b-table-column>
      <template v-slot:detail="props">
        <slot name="detail" :row="props.row" />
      </template>
      <section class="section" slot="empty">
        <div class="content has-text-grey has-text-centered">
          <template v-if="isLoading">
            <p>
              <b-icon icon="dots-horizontal" size="is-large" />
            </p>
            <p>Fetching data...</p>
          </template>
          <template v-else>
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>Nothing's here&hellip;</p>
          </template>
        </div>
      </section>
    </b-table>
  </div>
</template>

<script>
import pluralize from 'pluralize'
import { kebabToSnake } from '@/utils/helper'
import { debounce, throttle } from 'lodash'

import ModalBox from '@/components/base/ModalBox'

export default {
  name: 'FlTable',
  components: { ModalBox },
  props: {
    prefix: {
      type: String,
      default: ''
    },
    titleKey: {
      type: String,
      default: 'name'
    },
    namespace: {
      type: String,
      default: ''
    },
    service: {
      type: Object,
      default: () => ({})
    },
    columns: {
      type: Array,
      default: () => [{
        label: 'Name',
        key: 'name'
      }]
    },
    hasUpdate: {
      type: Boolean,
      default: true
    },
    hasDelete: {
      type: Boolean,
      default: true
    },
    checkable: {
      type: Boolean,
      default: false
    },
    isPaginated: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Object,
      default: () => ({})
    },
    filterFunc: {
      type: Function,
      default: () => true
    }
  },
  data() {
    return {
      isModalActive: false,
      trashObject: null,
      data: [],
      isLoading: false,
      total: 0,
      perPage: 10,
      currPage: 1,
      checkedRows: []
    }
  },
  computed: {
    hasDetail() {
      return !!this.$scopedSlots.detail
    },
    pluralNamespace() {
      return pluralize(kebabToSnake(this.namespace))
    },
    trashObjectName() {
      if (this.trashObject) {
        return this.trashObject[this.titleKey]
      }

      return null
    }
  },
  mounted() {
    if (this.service) {
      this.initData()
    }
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.debouncedInitData()
      }
    },
    filterFunc() {
      this.debouncedInitData()
    }
  },
  methods: {
    toggleDetails(row) {
      return this.$refs['table'].toggleDetails(row)
    },
    onPageChange(page) {
      this.currPage = page
      this.initData()
    },
    updateTo(row) {
      const params = { id: row.id }
      params[this.namespace] = row
      return {
        name: this.prefix.length === 0 ? `${this.namespace}.update` : `${this.prefix}.${this.namespace}.update`,
        params: params
      }
    },
    changeRowColor() {
      const rows = document.getElementsByClassName('has-color')
      for (let i = 0;i < rows.length;i++) {
        const classes = rows[i].getAttribute('class').split(' ')
        const color = classes.find(item => item.includes('#'))
        rows[i].setAttribute('style', `background-color: ${color}`)
      }
    },
    debouncedInitData: debounce(function () { this.initData() }, 500),
    refresh: throttle(function () { this.initData() }, 500),
    initData() {
      let filter = this.filter
      if (this.isPaginated) {
        filter.limit = this.perPage
        filter.offset = (this.currPage - 1) * this.perPage
      }
      this.isLoading = true
      this.service.read(filter)
        .then(r => {
          if (r.data && r.data[this.pluralNamespace]) {
            this.data = r.data[this.pluralNamespace].filter(this.filterFunc)
            this.total = r.data.total
          } else {
            this.data = []
          }
        })
        .catch(e => {
          this.$buefy.toast.open({
            message: e.response.data,
            type: 'is-danger'
          })
        })
        .finally(() => {
          this.isLoading = false
          this.$nextTick(this.changeRowColor())
          this.$emit('on-initialized', this.data)
        })
    },
    trashModal(trashObject) {
      this.trashObject = trashObject
      this.isModalActive = true
    },
    trashConfirm() {
      this.isModalActive = false

      this.service.delete(this.trashObject.id || this.trashObject.name)
        .then(() => {
          this.initData()
          this.$buefy.snackbar.open({
            message: 'Deleted',
            queue: false
          })
        })
    },
    trashCancel() {
      this.isModalActive = false
    },
    onRowClick(row) {
      this.$emit('on-row-click', row)
    }
  }
}
</script>
